import auth0, { Auth0Callback, Auth0Error, WebAuth } from 'auth0-js'
import { navigate } from 'gatsby'
import { getUserMe } from './user'

const isBrowser = typeof window !== 'undefined'
const webAuthConfig = {
    domain: process.env.GATSBY_AUTH0_DOMAIN || '',
    clientID: process.env.GATSBY_AUTH0_CLIENT_ID || '',
    redirectUri: process.env.GATSBY_AUTH0_CALLBACK_URL,
    responseType: 'token id_token',
    scope: 'openid profile email',
    audience: process.env.GATSBY_AUTH0_AUDIENCE,
}

const auth: WebAuth | null = isBrowser ? new auth0.WebAuth(webAuthConfig) : null
const ACCOUNT_SETUP_DELAY_MS = 2500
const MIN_ACCOUNT_SETUP_VISIBLE_MS = 10000
const ACCOUNT_SETUP_FLOW_KEY = 'accountSetupFlow'

const isAuthenticated = () => {
    if (!isBrowser) {
        return
    }
    const hasLoggedIn = localStorage.getItem('isLoggedIn') === 'true'
    let hasBusinessId = null
    try {
        const data = JSON.parse(localStorage.getItem('userMe'))
        hasBusinessId = data.current_business_id
        // eslint-disable-next-line no-empty
    } catch {}
    return hasLoggedIn && hasBusinessId
}

const login = (
    skipCache: boolean = false,
    silent: boolean = false,
    setupFlow?: 'login' | 'business-switch' | 'secondary-business-created'
) => {
    localStorage.removeItem('auth0LogoutRoute')
    localStorage.setItem(ACCOUNT_SETUP_FLOW_KEY, setupFlow || 'login')
    if (!isBrowser) {
        return
    }
    if (!auth) {
        return
    }
    auth.authorize({
        redirectUri: webAuthConfig.redirectUri,
        prompt: silent ? 'none' : undefined,
    })
    getUserMe(false, skipCache)
}

const refreshToken = (
    cb = () => {
        return
    }
) => {
    if (!isBrowser) {
        return
    }
    if (!auth) {
        return
    }
    auth.renewAuth({ redirectUri: webAuthConfig.redirectUri }, cb)
}

// interface AuthResultProps {
//     accessToken: string
//     idToken: string
//     expiresIn: number
//     expireIn: string
//     refreshToken: string
//     idTokenPayload: any
// }

const setSession = (
    cb = () => {
        return
    }
): Auth0Callback<never, Auth0Error> => {
    return (err, authResult) => {
        if (!isBrowser) {
            return
        }
        const setupFlow = localStorage.getItem(ACCOUNT_SETUP_FLOW_KEY)
        localStorage.removeItem(ACCOUNT_SETUP_FLOW_KEY)
        if (err) {
            navigate('/settings')
            return
        }

        if (authResult && authResult.accessToken && authResult.idToken) {
            const expiresAt = authResult.expiresIn * 1000 + new Date().getTime()
            localStorage.setItem('auth0AccessToken', authResult.accessToken)
            localStorage.setItem('auth0RefreshToken', authResult.refreshToken)
            localStorage.setItem('auth0IdToken', authResult.idToken)
            localStorage.setItem('auth0ExpiresAt', JSON.stringify(expiresAt))
            localStorage.setItem('auth0ExpiresIn', authResult.expireIn)
            localStorage.setItem(
                'auth0User',
                JSON.stringify(authResult.idTokenPayload)
            )
            localStorage.setItem('isLoggedIn', 'true')
            let setupStartedAt = 0
            const startAccountSetup = () => {
                if (setupStartedAt) return
                setupStartedAt = Date.now()
                cb()
            }
            const accountSetupTimer =
                setupFlow === 'business-switch' ||
                setupFlow === 'secondary-business-created'
                    ? undefined
                    : window.setTimeout(
                          startAccountSetup,
                          ACCOUNT_SETUP_DELAY_MS
                      )
            getUserMe(false, true).then((user) => {
                if (accountSetupTimer) {
                    window.clearTimeout(accountSetupTimer)
                }
                const createdAt = user?.created_at
                const hasTimezone =
                    createdAt && /(?:Z|[+-]\d{2}:?\d{2})$/i.test(createdAt)
                const createdAtMs = createdAt
                    ? new Date(
                          hasTimezone ? createdAt : `${createdAt}Z`
                      ).getTime()
                    : 0
                const accountAgeMs = Date.now() - createdAtMs
                const accountCreatedRecently =
                    Number.isFinite(createdAtMs) &&
                    createdAtMs > 0 &&
                    accountAgeMs >= 0 &&
                    accountAgeMs < 10 * 60 * 1000
                const shouldShowAccountSetup =
                    setupFlow === 'login' && accountCreatedRecently
                if (!shouldShowAccountSetup) {
                    navigate('/settings/')
                    return
                }
                startAccountSetup()
                window.setTimeout(
                    () => navigate('/settings/'),
                    Math.max(
                        MIN_ACCOUNT_SETUP_VISIBLE_MS -
                            (Date.now() - setupStartedAt),
                        0
                    )
                )
            })
        }
    }
}

const handleAuthentication = (
    cb = () => {
        return
    }
) => {
    if (!isBrowser) {
        return
    }
    if (!auth) {
        return
    }
    auth.parseHash(setSession(cb))
}

const getProfile = () => {
    let value = localStorage.getItem('auth0User')
    if (!value) {
        value = '{"current_business_id": null, "name": null}'
    }
    return JSON.parse(value)
}

const logout = (returnTo?: string) => {
    localStorage.setItem('isLoggedIn', 'false')
    localStorage.removeItem('auth0AccessToken')
    localStorage.removeItem('auth0RefreshToken')
    localStorage.removeItem('auth0IdToken')
    localStorage.removeItem('auth0ExpiresAt')
    localStorage.removeItem('auth0ExpiresIn')
    localStorage.removeItem('auth0User')
    localStorage.removeItem('userMe')
    localStorage.removeItem(ACCOUNT_SETUP_FLOW_KEY)
    if (returnTo) {
        localStorage.setItem('auth0LogoutRoute', returnTo)
    }
    if (!auth) {
        return
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const location = (window as any).location
    auth.logout({ returnTo: `${location?.origin}/auth/logout` })
}

const getAuthorizationHeader = () => {
    const expiresAt = JSON.parse(localStorage.getItem('auth0ExpiresAt') || '0')
    if (expiresAt < new Date().getTime() / 1000) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const location = (window as any).location
        if (location.pathname.startsWith('/settings')) {
            navigate('/settings')
        }
        console.log('Token expired')
    }
    const accessToken = localStorage.getItem('auth0AccessToken')
    const obj = {
        headers: {},
    }
    if (accessToken) {
        obj.headers = {
            Authorization: `Bearer ${accessToken}`,
        }
    }
    return obj
}

type Permission = string

type Auth0TokenClaims = {
    permissions?: ReadonlyArray<Permission>
    scope?: string
    exp?: number
    iat?: number
    sub?: string
    iss?: string
    aud?: string | string[]
    [k: string]: unknown
}

type JwtDecoded<T = Auth0TokenClaims> = {
    header: Record<string, unknown>
    payload: T
    signature: string
}

const decodeBase64Url = (b64url: string): string => {
    const b64 = b64url
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(b64url.length / 4) * 4, '=')
    return atob(b64)
}

type ParseJwt = <T = Auth0TokenClaims>(token: string) => JwtDecoded<T>

const parseJwt: ParseJwt = (token) => {
    const parts = token.split('.')
    if (parts.length !== 3) throw new Error('Invalid JWT format')

    const [encodedHeader, encodedPayload, signature] = parts
    const header = JSON.parse(decodeBase64Url(encodedHeader)) as Record<
        string,
        unknown
    >
    const payload = JSON.parse(decodeBase64Url(encodedPayload)) as T

    return { header, payload, signature }
}

const getStoredToken = (key = 'auth0AccessToken'): string | null =>
    localStorage.getItem(key)

const permissionsFromClaims = (claims: Auth0TokenClaims): Permission[] => {
    if (Array.isArray(claims.permissions)) return [...claims.permissions]
    if (claims.scope) return claims.scope.split(' ').filter(Boolean)
    return []
}

const getStoredPermissions = (key = 'auth0AccessToken'): Permission[] => {
    try {
        const token = getStoredToken(key)
        if (!token) return []
        const { payload } = parseJwt<Auth0TokenClaims>(token)
        return permissionsFromClaims(payload)
    } catch {
        return []
    }
}

export {
    handleAuthentication,
    getAuthorizationHeader,
    isAuthenticated,
    login,
    refreshToken,
    logout,
    getProfile,
    getStoredPermissions,
}
